import { getPosts } from '@/services/posts'
import { clearPortalSelectionForPosts } from '@/services/portals'

function findPostInList (postsList, postId) {
  if (!postsList || !postsList.length) {
    return null
  }
  const normalizedId = String(postId)
  return postsList.find(post => String(post.id) === normalizedId)
}

function hasPortalSelection (post) {
  if (!post) {
    return false
  }
  const count = post.nbDiffusions != null ? post.nbDiffusions : post.nb_diffusions
  return Number(count) > 0
}

function isPostActive (post) {
  if (!post) {
    return false
  }
  if (post.etat === 'A') {
    return true
  }
  if (post.published === true) {
    return true
  }
  return post.etatAnnonce === 'Active'
}

/**
 * Résout les IDs cibles (switch unitaire vs sélection multiple).
 *
 * @param {number|number[]|null} id
 * @param {number[]} selectedIds
 * @returns {number[]}
 */
export function resolveTargetPostIds (id, selectedIds) {
  if (Array.isArray(id)) {
    return id
  }
  if (id != null) {
    return [id]
  }
  return selectedIds || []
}

/**
 * Retourne les annonces qui passent de l'état actif (A) en brouillon (B).
 *
 * @param {number[]} postIds
 * @param {object[]} postsList
 * @returns {object[]}
 */
export function getPostsGoingToDraft (postIds, postsList) {
  if (!postIds.length || !postsList || !postsList.length) {
    return []
  }

  return postIds
    .map(postId => findPostInList(postsList, postId))
    .filter(post => isPostActive(post))
}

/**
 * Retourne les annonces actives (A) passant en brouillon avec sélection portails.
 *
 * @param {number[]} postIds
 * @param {object[]} postsList
 * @returns {object[]}
 */
export function getPostsGoingToDraftWithPortals (postIds, postsList) {
  if (!postIds.length || !postsList || !postsList.length) {
    return []
  }

  return postIds
    .map(postId => findPostInList(postsList, postId))
    .filter(post => isPostActive(post) && hasPortalSelection(post))
}

/**
 * Étend la liste d'IDs avec les lots d'un programme promoteur.
 *
 * @param {number[]} postIds
 * @param {object[]} postsList
 * @param {object|null} sitimmoUser
 * @returns {Promise<number[]>}
 */
export async function expandPostIdsWithLots (postIds, postsList, sitimmoUser) {
  if (!sitimmoUser || sitimmoUser.metier !== 'PROMOTEUR') {
    return postIds
  }

  const expanded = new Set(postIds)

  for (const postId of postIds) {
    const post = findPostInList(postsList, postId)
    if (!post || post.annonceParente != null) {
      continue
    }

    const response = await getPosts({ annonceParente: postId, limit: 100 })
    if (response && response.list && response.list.length) {
      response.list.forEach(lot => expanded.add(lot.id))
    }
  }

  return [...expanded]
}

/**
 * Désactive la sélection portails pour les annonces passant en brouillon.
 *
 * @param {object} params
 * @returns {Promise<{type: string, message?: *}>}
 */
export async function clearPortalSelectionBeforeDraft ({
  id,
  selectedIds,
  postsList,
  sitimmoUser
}) {
  const postIds = resolveTargetPostIds(id, selectedIds)
  const postsGoingToDraft = getPostsGoingToDraft(postIds, postsList)

  if (!postsGoingToDraft.length) {
    return { type: 'success' }
  }

  const idsToClear = await expandPostIdsWithLots(
    postsGoingToDraft.map(post => post.id),
    postsList,
    sitimmoUser
  )

  return clearPortalSelectionForPosts(idsToClear)
}

/**
 * Indique si la modale de conservation portails doit s'afficher.
 *
 * @param {number|number[]|null} id
 * @param {number[]} selectedIds
 * @param {object[]} postsList
 * @returns {boolean}
 */
export function shouldShowDraftPortalDialog (id, selectedIds, postsList) {
  const postIds = resolveTargetPostIds(id, selectedIds)
  return getPostsGoingToDraft(postIds, postsList).length > 0
}

/**
 * Nombre d'annonces concernées par la modale.
 *
 * @param {number|number[]|null} id
 * @param {number[]} selectedIds
 * @param {object[]} postsList
 * @returns {number}
 */
export function getDraftPortalDialogPostCount (id, selectedIds, postsList) {
  const postIds = resolveTargetPostIds(id, selectedIds)
  return getPostsGoingToDraft(postIds, postsList).length
}
